import React, { useState } from 'react'

import { parseGoTo, type GoToResult } from './goTo'

type GoToBoxProps = {
  /** Fired on submit (Enter) with the parsed intent. */
  onGo: (result: GoToResult) => void
  /** Fired as the user types when the entry is free text, for live filtering. */
  onLiveText: (text: string) => void
}

/**
 * Search box above the variant table (GEN-3103). Free text live-filters the
 * table as you type; pressing Enter on a locus / variant id / rsID jumps the
 * selection and the IGV browser instead.
 */
const GoToBox: React.FC<GoToBoxProps> = ({ onGo, onLiveText }) => {
  const [value, setValue] = useState('')

  const onChange = (next: string) => {
    setValue(next)
    const parsed = parseGoTo(next)
    onLiveText(parsed.kind === 'text' ? parsed.value : '')
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = value.trim()
    if (trimmed) onGo(parseGoTo(trimmed))
  }

  return (
    <form onSubmit={submit} className="flex items-center gap-1" role="search">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Go to locus / variant / rsID, or filter..."
        aria-label="Go to locus, variant, rsID, or filter the table"
        title={
          'Accepted formats:\n' +
          'locus: chr1:55,516,888\n' +
          'interval: chr2:152341851-152591002\n' +
          'variant id: chr1-12345-A-G\n' +
          'SeqOne VCF copy: 2-239176431-T-G\n' +
          'SeqOne gDNA HGVS: chr2(GRCh38):g.239176431T>G\n' +
          'rsID: rs113993960\n' +
          'Anything else filters the table.'
        }
        data-testid="goto-input"
        className="border-border-subtle focus:border-brand-500 w-full rounded border px-2 py-1 text-xs outline-none"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="text-text-subtle hover:text-text-heading px-1 text-xs"
          aria-label="Clear search"
          data-testid="goto-clear"
        >
          Clear
        </button>
      )}
    </form>
  )
}

export default GoToBox
